import type { ReactElement } from "react"
import { useContext } from "react"
import BaseContainer from "@gatsby-plugin-generic-page/components/BaseContainer"
import BaseSection from "@gatsby-plugin-generic-page/components/BaseSection"
import MarkdownText from "@gatsby-plugin-generic-page/components/Markdown/Text"
import SwitchPeriodAndTogglePriceType from "@gatsby-plugin-generic-page/components/SwitchPeriodAndTogglePriceType"
import type { ParametersTableSectionFlatData } from "@gatsby-plugin-generic-page/fragments/parametersTableSection"
import getPeriodsByPeriodVariants from "@gatsby-plugin-generic-page/helpers/getPeriodsByPeriodVariants"
import { Text } from "@ionos-web-design-system/react"
import ContentSwitcher from "~/components/ContentSwitcher"
import ParametersTableUds from "~/components/ParametersTableUds"
import SwitchContentContext from "~/context/SwitchContentContext"
import { cn } from "~/helpers/cn"
import isEmpty from "~/lib/isEmpty"
import type { PeriodName } from "~/types/squidex"

export default function ParametersTableSection({
  title,
  comparableProducts,
  recommendedComparableProduct,
  period,
  hasTogglePriceType,
  text,
  isTextCentered,
  sectionSettings,
}: ParametersTableSectionFlatData): ReactElement<ParametersTableSectionFlatData> {
  const context = useContext(SwitchContentContext)

  const { periodVariants } = context || {}

  const periods = getPeriodsByPeriodVariants(periodVariants)

  const hasPeriodSwitcher = !isEmpty(periods)

  return (
    <BaseSection {...sectionSettings}>
      <BaseContainer {...sectionSettings?.containerSettings}>
        <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-4 md:gap-8">
          {!isEmpty(title) ? (
            <Text variant="heading5xl" className="md:grow" asChild>
              <h2>{title}</h2>
            </Text>
          ) : null}

          {hasTogglePriceType || hasPeriodSwitcher ? (
            <SwitchPeriodAndTogglePriceType
              hasTogglePriceType={hasTogglePriceType}
              hasNoMarginBottom
            />
          ) : null}
        </div>

        {!isEmpty(context) && hasPeriodSwitcher ? (
          <ContentSwitcher items={periods} selected={context.currentKey || ""}>
            {(selected) => {
              return (
                <div className="mt-4">
                  <ParametersTableUds
                    products={comparableProducts}
                    selectedPeriod={selected.period as PeriodName}
                    recommendedComparableProductId={
                      recommendedComparableProduct?.[0]?.id
                    }
                  />
                </div>
              )
            }}
          </ContentSwitcher>
        ) : (
          <div className="mt-4">
            <ParametersTableUds
              products={comparableProducts}
              selectedPeriod={period?.period as PeriodName | undefined}
              recommendedComparableProductId={
                recommendedComparableProduct?.[0]?.id
              }
            />
          </div>
        )}

        {!isEmpty(text) ? (
          <div
            className={cn(
              "mt-5 flex flex-col",
              isTextCentered
                ? "items-center text-center"
                : "items-start text-left"
            )}
          >
            <MarkdownText>{text}</MarkdownText>
          </div>
        ) : null}
      </BaseContainer>
    </BaseSection>
  )
}
